import { Feature as OlFeature } from "ol";
import { Geometry } from "ol/geom";
import { getSharedStyle, selectedClusterStyleFunction } from "../../module/map/olmap";

/**
 * @author Kim Young Jun
 * @date 2025-07-21
 * @description 지도 뷰 마커 하이라이팅 매니저
 * - 선택된 마커/클러스터의 상태 관리
 * - 이전 선택 해제, 새로운 선택 적용 로직 캡슐화
 * - 장소 수정 모드에서의 클러스터 이동 제어
 * - 편집 중인 마커의 임시 좌표 관리
 */
export class MarkerHighlightManager {
    private selectedFeature: OlFeature<Geometry> | null = null;
    private previousSelectedFeatures: OlFeature<Geometry>[] = [];
    private selectedClusterFeatures: OlFeature<Geometry>[] = [];
    private isEditMode: boolean = false;

    // 25.08.27 | Kim Young Jun | 편집 중인 마커의 임시 좌표 저장
    private editingFeatureId: string | number | null = null;
    private temporaryCoordinates: number[] | null = null;
    private originalCoordinates: number[] | null = null;

    // FloatingSideSheet 닫기를 위한 콜백
    private onCloseFloatingSideSheet?: () => void;

    // OpenLayers Map 객체 참조
    private mapInstance: any = null;

    /**
     * 현재 선택된 피처를 반환
     */
    getSelectedFeature(): OlFeature<Geometry> | null {
        return this.selectedFeature;
    }

    /**
     * OpenLayers Map 인스턴스 설정
     * @param mapInstance OpenLayers Map 객체
     */
    setMapInstance(mapInstance: any): void {
        this.mapInstance = mapInstance;
    }

    /**
     * FloatingSideSheet 닫기 콜백 설정
     * @param callback FloatingSideSheet 닫기 함수
     */
    setCloseFloatingSideSheetCallback(callback: () => void): void {
        this.onCloseFloatingSideSheet = callback;
    }

    // 25.08.27 | Kim Young Jun | 편집 중인 마커 구분 및 클러스터링/마커 구분을 위한 기능 추가

    /**
     * 편집 모드 상태 설정
     * @param isEditMode 편집 모드 여부
     */
    setEditMode(isEditMode: boolean): void {
        this.isEditMode = isEditMode;
        
        // 편집 모드가 비활성화되면 편집 중인 피처 정보 초기화
        if (!isEditMode) {
            this.editingFeatureId = null;
            this.temporaryCoordinates = null;
            this.originalCoordinates = null;
        }
    }

    /**
     * 편집 모드 상태 반환
     */
    getEditMode(): boolean {
        return this.isEditMode;
    }

    /**
     * 피처가 클러스터인지 확인
     * @param feature 확인할 피처
     * @returns 클러스터 여부
     */
    isClusterFeature(feature: OlFeature<Geometry>): boolean {
        const features = feature.get('features');
        return Array.isArray(features) && features.length > 1;
    }

    /**
     * 편집 모드에서 피처 이동 가능 여부 확인
     * @param feature 확인할 피처
     * @param selectedId 선택된 피처 ID
     * @returns 이동 가능 여부
     */
    canTranslateInEditMode(feature: OlFeature<Geometry>, selectedId: string | number): boolean {
        if (!this.isEditMode) {
            return true; // 편집 모드가 아니면 모든 피처 이동 가능
        }

        // 클러스터인 경우 이동 불가
        if (this.isClusterFeature(feature)) {
            return false;
        }

        // 단일 마커인 경우, 선택된 마커만 이동 가능
        const features = feature.get('features');
        if (features && features.length === 1) {
            const featureId = features[0].getId();
            return featureId === selectedId || featureId === String(selectedId);
        }

        return false;
    }

    /**
     * 편집 중인 마커 설정
     * @param featureId 편집할 피처의 ID
     * @param originalCoords 원본 좌표
     */
    startEditingFeature(featureId: string | number, originalCoords: number[]): void {
        this.editingFeatureId = featureId;
        this.originalCoordinates = [...originalCoords];
        this.temporaryCoordinates = [...originalCoords];
    }

    /**
     * 편집 중인 마커의 임시 좌표 업데이트
     * @param coordinates 새로운 좌표
     */
    updateTemporaryCoordinates(coordinates: number[]): void {
        if (this.editingFeatureId) {
            this.temporaryCoordinates = [...coordinates];
        }
    }

    /**
     * 편집 중인 마커의 임시 좌표 반환
     * @param featureId 피처 ID
     * @returns 임시 좌표 또는 null
     */
    getTemporaryCoordinates(featureId: string | number): number[] | null {
        if (this.editingFeatureId === featureId || this.editingFeatureId === String(featureId)) {
            return this.temporaryCoordinates ? [...this.temporaryCoordinates] : null;
        }
        return null;
    }

    /**
     * 편집 중인 마커인지 확인
     * @param featureId 피처 ID
     * @returns 편집 중 여부
     */
    isEditingFeature(featureId: string | number): boolean {
        return this.editingFeatureId === featureId || this.editingFeatureId === String(featureId);
    }

    /**
     * 편집 완료 - 임시 좌표를 실제 좌표로 적용
     */
    finishEditing(): void {
        this.editingFeatureId = null;
        this.temporaryCoordinates = null;
        this.originalCoordinates = null;
    }

    /**
     * 편집 취소 - 원본 좌표로 복원
     * @returns 원본 좌표 또는 null
     */
    cancelEditing(): number[] | null {
        const coords = this.originalCoordinates ? [...this.originalCoordinates] : null;
        this.editingFeatureId = null;
        this.temporaryCoordinates = null;
        this.originalCoordinates = null;
        return coords;
    }

    /**
     * 모든 선택 상태를 초기화
     * @param closeFloatingSheet FloatingSideSheet 닫을지 여부 (기본값: false)
     */
    clearAllSelections(closeFloatingSheet: boolean = false): void {
        // console.log("@@@@@@@@ [MarkerHighlightManager] clearAllSelections()");
        // console.log("     >>> this.selectedFeature:", this.selectedFeature);
        // console.log("     >>> this.selectedClusterFeatures:", this.selectedClusterFeatures);

        // 현재 선택된 피처 해제
        if (this.selectedFeature) {
            // console.log("@@@@@@@@ [MarkerHighlightManager] clearing selectedFeature:", this.selectedFeature.getId());
            this.selectedFeature.set("selected", false);
            // this.selectedFeature.setProperties({ selected: false });
            this.selectedFeature.unset("selected");
            this.selectedFeature = null;
        }

        // 이전에 선택된 클러스터 마커들 초기화
        this.clearPreviousSelections();

        // 선택된 클러스터 피처들 초기화 (중요: 클러스터에서 선택된 마커들도 해제)
        this.clearSelectedClusterFeatures();

        // FloatingSideSheet 닫기 (옵션에 따라)
        if (closeFloatingSheet && this.onCloseFloatingSideSheet) {
            this.onCloseFloatingSideSheet();
        }
    }

    /**
     * 단일 마커 선택 처리
     * @param feature 선택할 피처
     */
    selectSingleMarker(feature: OlFeature<Geometry> | undefined): void {
        // this.clearAllSelections();

        // 편집 모드에서는 클러스터 피처 선택을 제한
        if (this.isEditMode && feature && this.isClusterFeature(feature)) {
            // console.warn("@@@@@@@@ [MarkerHighlightManager] selectSingleMarker() 편집 모드에서는 클러스터 마커를 선택할 수 없습니다.");
            return;
        }

        this.clearAllSelections();

        // 이전 선택 해제
        if (this.selectedFeature) {
            // console.log("@@@@@@@@ [MarkerHighlightManager] selectSingleMarker() unset selectedMarker");
            this.selectedFeature.unset("selected");
        }

        // 새로운 선택 적용
        if (feature) {
            // console.log("@@@@@@@@ [MarkerHighlightManager] selectSingleMarker() set selectedMarker with ID:", feature.getId());
            
            // 25.09.08 | Kim Young Jun | 선택 상태를 확실하게 적용
            // feature.setProperties({ selected: true });
            feature.set("selected", true); // 추가로 직접 설정
            this.selectedFeature = feature;
            // this.selectedClusterFeatures.push(feature);
            
            // 스타일 변경을 즉시 반영
            feature.changed();
            // console.log("@@@@@@@@ [MarkerHighlightManager] selectSingleMarker() selectedMarker properties:", feature.getProperties());
        } else {
            this.selectedFeature = null;
        }

        // console.log("@@@@@@@@ [MarkerHighlightManager] selectSingleMarker()");
        // console.log("     >>> selectSingleMarker() this.selectedFeature", this.selectedFeature);
        // console.log("     >>> selectSingleMarker() this.selectedClusterFeatures", this.selectedClusterFeatures);
    }

    /**
     * 클러스터 선택 처리
     * @param clusterFeature 선택된 클러스터 피처
     * @param clusteredFeatures 클러스터 내부의 피처들
     */
        selectCluster(clusterFeature: OlFeature<Geometry>, clusteredFeatures: OlFeature<Geometry>[]): void {
        // console.log("[DBG] MarkerHighlightManager | selectCluster called with clusterFeature:", clusterFeature);

        // 편집 모드에서는 클러스터 선택을 제한
        if (this.isEditMode && this.isClusterFeature(clusterFeature)) {
            console.warn("[MarkerHighlightManager] 편집 모드에서는 클러스터를 선택할 수 없습니다.");
            return;
        }

        if (clusteredFeatures.length === 1) {
            // 단일 마커인 경우 - 이전 클러스터 선택들 해제
            this.clearPreviousSelections();
            
            // 단일 마커 선택 처리
            const singleFeature = clusteredFeatures[0];
            singleFeature.set("selected", true);
            // singleFeature.setProperties({ selected: true });
            this.selectedFeature = singleFeature;
        } else {
            // 실제 클러스터인 경우
            this.clearPreviousSelections();
            
            // 클러스터 스타일 적용을 위한 비동기 처리
            setTimeout(() => {
                const clusterPoints = (clusterFeature.get("selectcluserfeatures") as OlFeature[])
                    ?.filter((f: OlFeature) => f.getGeometry()?.getType() === "Point") || [];

                // 선택된 피처들을 추적 배열에 저장
                clusterPoints.forEach((pointFeature: OlFeature) => {
                    const originalFeature = pointFeature.get("features")?.[0] as OlFeature | undefined;
                    if (originalFeature?.get("selected")) {
                        this.previousSelectedFeatures.push(pointFeature);
                    }
                });

                // 클러스터 스타일 적용
                clusterFeature.setStyle(selectedClusterStyleFunction(clusterFeature));
                this.selectedClusterFeatures.push(clusterFeature);
            }, 0);
        }
    }


    /**
     * 특정 피처의 선택 상태 해제
     * @param feature 선택 해제할 피처
     */
    unselectFeature(feature: OlFeature<Geometry>): void {
        feature.unset("selected");
        
        // 현재 선택된 피처인 경우 상태 업데이트
        if (this.selectedFeature === feature) {
            this.selectedFeature = null;
        }

        // 이전 선택 배열에서 제거
        const index = this.previousSelectedFeatures.indexOf(feature);
        if (index > -1) {
            this.previousSelectedFeatures.splice(index, 1);
        }

        // 클러스터 선택 배열에서 제거
        const clusterIndex = this.selectedClusterFeatures.indexOf(feature);
        if (clusterIndex > -1) {
            this.selectedClusterFeatures.splice(clusterIndex, 1);
        }
    }

    /**
     * 피처 ID로 특정 피처 선택
     * @param features 검색할 피처 배열
     * @param targetId 선택할 피처의 ID
     */
    selectFeatureById(features: OlFeature<Geometry>[], targetId: string | number): boolean {
        const targetFeature = features.find(feature => {
            const featureId = feature.getId();
            return featureId === targetId || featureId === String(targetId);
        });

        if (targetFeature) {
            this.selectSingleMarker(targetFeature);
            return true;
        }
        
        return false;
    }

    /**
     * 이전에 선택된 클러스터 마커들 초기화
     */
    private clearPreviousSelections(): void {
        while (this.previousSelectedFeatures.length > 0) {
            const clusterFeature = this.previousSelectedFeatures.shift();
            if (!clusterFeature) continue;

            // 클러스터 내부의 피처를 기준으로 스타일 재설정
            const innerFeature = clusterFeature.get("features")?.[0];
            if (innerFeature) {
                const style = getSharedStyle(innerFeature);
                // 스타일을 초기화한 후 다시 설정 (하이라이팅 해제)
                clusterFeature.setStyle(undefined);
                clusterFeature.setStyle(style);
            }
        }
    }

    /**
     * 선택된 클러스터 피처들 초기화
     */
    private clearSelectedClusterFeatures(): void {
        // console.log("[MarkerHighlightManager] clearSelectedClusterFeatures this.selectedClusterFeatures: ", this.selectedClusterFeatures);
        // console.log("[MarkerHighlightManager] clearSelectedClusterFeatures this.selectedFeature: ", this.selectedFeature);
        
        this.selectedClusterFeatures.forEach(clusterFeature => {
            // 클러스터 내부의 개별 피처들의 selected 속성 해제
            const features = clusterFeature.get("features");
            if (Array.isArray(features)) {
                features.forEach((feature: OlFeature<Geometry>) => {
                    // console.log("[DBG] MarkerHighlightManager | unsetting selected on feature:", feature.getId());
                    feature.set("selected", false);
                    // feature.setProperties({ selected: false });
                    feature.unset("selected");
                });
            }
            
            // 클러스터 기본 스타일로 복원
            const innerFeature = clusterFeature.get("features")?.[0];
            if (innerFeature) {
                const style = getSharedStyle(innerFeature);
                clusterFeature.setStyle(undefined);
                clusterFeature.setStyle(style);
            }
            
            // 클러스터 피처 자체의 선택 상태도 해제
            // clusterFeature.setProperties({ selected: false });
            clusterFeature.set("selected", false);
            clusterFeature.unset("selected");
        });
        this.selectedClusterFeatures = [];
    }

    /**
     * 디버깅용 현재 상태 출력
     */
    getDebugInfo(): {
        selectedFeature: string | null;
        previousSelectedCount: number;
        selectedClusterCount: number;
        isEditMode: boolean;
        editingFeatureId: string | number | null;
        hasTemporaryCoordinates: boolean;
    } {
        return {
            selectedFeature: this.selectedFeature?.getId()?.toString() || null,
            previousSelectedCount: this.previousSelectedFeatures.length,
            selectedClusterCount: this.selectedClusterFeatures.length,
            isEditMode: this.isEditMode,
            editingFeatureId: this.editingFeatureId,
            hasTemporaryCoordinates: this.temporaryCoordinates !== null,
        };
    }

    /**
     * 카테고리 전환 시 현재 선택된 마커가 새로운 카테고리에 포함되는지 검사
     * @param allPlaceData POI 데이터 객체 (key: XID, value: PlaceInfoSet)
     * @param mapId 지도 ID
     * @param selectedCategories 선택된 카테고리 배열 (전체 선택 시 빈 배열 또는 전체 카테고리 포함)
     * @param allCategories 전체 카테고리 옵션 배열
     * @returns 현재 선택이 새 카테고리에서 유효한지 여부와 선택된 POI의 XID
     */
    validateCurrentSelectionByCategory(
        allPlaceData: any, 
        mapId: string, 
        selectedCategories: string[], 
        allCategories: any[]
    ): { isValid: boolean; selectedXID?: number; poiCategory?: string } {
        // console.log("[DEBUG] MarkerHighlightManager.validateCurrentSelectionByCategory() - 시작");
        
        if (!this.selectedFeature) {
            // console.log("[DEBUG] validateCurrentSelectionByCategory() - 선택된 피처가 없음");
            return { isValid: false };
        }

        const selectedId = this.selectedFeature.getId();

        if (!selectedId) {
            // console.log("[DEBUG] validateCurrentSelectionByCategory() - 선택된 피처의 ID가 없음");
            return { isValid: false };
        }

        // 피처 ID에서 XID 추출 (형식: "mapId.XID")
        const selectedIdStr = selectedId.toString();
        const parts = selectedIdStr.split('.');
        
        if (parts.length !== 2 || parts[0] !== mapId) {
            // console.log("[DEBUG] validateCurrentSelectionByCategory() - 피처 ID 형식이 올바르지 않거나 다른 지도의 피처");
            return { isValid: false };
        }

        const selectedXID = parseInt(parts[1]);
        // console.log("[DEBUG] validateCurrentSelectionByCategory() - 선택된 XID:", selectedXID);

        // POI 데이터에서 해당 XID가 존재하는지 확인
        // console.log("[DEBUG] validateCurrentSelectionByCategory() - allPlaceData:", allPlaceData);
        const poiData = allPlaceData[selectedXID] || allPlaceData[selectedXID.toString()];
        if (!poiData) {
            // console.log("[DEBUG] validateCurrentSelectionByCategory() - POI 데이터에서 찾을 수 없음");
            return { isValid: false };
        }

        // POI의 카테고리 정보 추출
        const poiCategory = poiData.primaryInfo?.category || poiData.category;
        // console.log("[DEBUG] validateCurrentSelectionByCategory() - POI 카테고리:", poiCategory);
        // console.log("[DEBUG] validateCurrentSelectionByCategory() - 선택된 카테고리:", selectedCategories);

        // 전체 카테고리 선택인지 확인 (빈 배열이거나 모든 카테고리가 포함된 경우)
        const isAllCategoriesSelected = selectedCategories.length === 0 || 
                                       selectedCategories.length === allCategories.length ||
                                       selectedCategories.includes('전체');

        // 카테고리 포함 여부 검사
        let isIncluded = false;
        if (isAllCategoriesSelected) {
            // 전체 카테고리 선택 시 모든 POI 포함
            isIncluded = true;
            // console.log("[DEBUG] validateCurrentSelectionByCategory() - 전체 카테고리 선택됨");
        } else {
            // 특정 카테고리들이 선택된 경우, POI의 카테고리가 선택된 카테고리에 포함되는지 확인
            isIncluded = selectedCategories.includes(poiCategory);
            // console.log("[DEBUG] validateCurrentSelectionByCategory() - 카테고리 포함 여부:", isIncluded);
        }

        const result = {
            isValid: isIncluded,
            selectedXID: selectedXID,
            poiCategory: poiCategory
        };
        
        // console.log("[DEBUG] validateCurrentSelectionByCategory() - 결과:", result.isValid ? "유지" : "해제");
        
        return result;
    }

    /**
     * 현재 선택된 마커의 ID 반환
     */
    getSelectedMarkerId(): string | number | null {
        return this.selectedFeature?.getId() ?? null;
    }
}
